import React from "react";
import {
  fetchWithSupabaseAuth,
  getSupabaseSession,
  subscribeToSupabaseAuthChanges,
} from "../lib/supabaseClient.js";
import {
  buildAccessState,
  EMPTY_ACCESS_STATE,
  hasAnyPermission as accessHasAnyPermission,
  hasPermission as accessHasPermission,
} from "../lib/accessControl.js";
import {
  clearAccessSessionCache,
  readAccessSessionCacheForSession,
  resolveAccessSessionUserId,
  writeAccessSessionCache,
} from "../lib/accessSessionCache.js";

const ACCESS_LOAD_TIMEOUT_MS = 20000;
const ACCESS_LOAD_TIMEOUT_RETRY_DELAY_MS = 400;
const ACCESS_DEBUG_ENABLED = typeof import.meta === "object" && Boolean(import.meta.env?.DEV);
const EMPTY_ACCESS_ERROR_META = Object.freeze({
  code: "",
  isAuthError: false,
  status: null,
});

class AccessRequestError extends Error {
  constructor(message, {
    code = "request_failed",
    isAuthError = false,
    status = null,
  } = {}) {
    super(message);
    this.name = "AccessRequestError";
    this.code = code;
    this.isAuthError = isAuthError === true;
    this.status = Number.isFinite(status) ? Number(status) : null;
  }
}

function logAccessDebug(event, details = undefined) {
  if (!ACCESS_DEBUG_ENABLED || typeof console === "undefined" || typeof console.info !== "function") {
    return;
  }

  if (details === undefined) {
    console.info("[useAccess]", event);
    return;
  }

  console.info("[useAccess]", event, details);
}

function buildAccessErrorMeta(error) {
  if (error instanceof AccessRequestError) {
    return {
      code: error.code,
      isAuthError: error.isAuthError,
      status: error.status,
    };
  }

  return EMPTY_ACCESS_ERROR_META;
}

function hasResolvedAccessState(access) {
  return Boolean(access?.activeRole)
    || (Array.isArray(access?.permissions) && access.permissions.length > 0)
    || (typeof access?.source === "string" && access.source.trim())
    || (typeof access?.userId === "string" && access.userId.trim());
}

function canPreserveResolvedAccessForUserId(access, userId) {
  const accessUserId = typeof access?.userId === "string" ? access.userId.trim() : "";
  const expectedUserId = typeof userId === "string" ? userId.trim() : "";

  return Boolean(accessUserId)
    && accessUserId === expectedUserId
    && hasResolvedAccessState(access);
}

function canPreserveResolvedAccessForSession(access, session) {
  return canPreserveResolvedAccessForUserId(access, resolveAccessSessionUserId(session));
}

function resolveAccessErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (payload && typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

function resolveAccessRequestError(error, {
  fallbackMessage = "Impossible de charger les accès.",
  payload = null,
  response = null,
} = {}) {
  if (error instanceof AccessRequestError || error?.name === "AbortError") {
    return error;
  }

  const status = typeof response?.status === "number"
    ? response.status
    : (typeof error?.status === "number" ? error.status : null);
  const isAuthError = status === 401;
  const code = status === 401
    ? "unauthorized"
    : status === 403
      ? "forbidden"
      : "request_failed";
  const message = resolveAccessErrorMessage(payload, error instanceof Error && error.message.trim()
    ? error.message.trim()
    : fallbackMessage);

  return new AccessRequestError(message, {
    code,
    isAuthError,
    status,
  });
}

function isValidAccessPayload(payload, { expectedUserId = "" } = {}) {
  if (
    !payload
    || typeof payload !== "object"
    || Array.isArray(payload)
    || !Array.isArray(payload?.permissions)
  ) {
    return false;
  }

  const userId = typeof payload.user_id === "string" ? payload.user_id.trim() : "";
  const activeRole = typeof payload.active_role === "string" ? payload.active_role.trim().toLowerCase() : "";
  const source = typeof payload.source === "string" ? payload.source.trim() : "";
  const accountTypeIsValid = payload.account_type === null
    || (typeof payload.account_type === "string" && payload.account_type.trim());

  if (
    !userId
    || !source
    || !accountTypeIsValid
    || !payload.permissions.every((permissionKey) => typeof permissionKey === "string" && permissionKey.trim())
    || typeof payload.dispatcher_workspace_access !== "boolean"
    || typeof payload.driver_portal_access !== "boolean"
    || !(payload.active_role === null || activeRole)
    || (expectedUserId && userId !== expectedUserId)
  ) {
    return false;
  }

  // Un Admin sans aucune permission est un état transitoire/incomplet, jamais
  // une réponse d'accès exploitable pour construire la navigation.
  return activeRole !== "admin" || payload.permissions.length > 0;
}

function waitForAccessRetryDelay(signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const abortError = new Error("The operation was aborted.");
      abortError.name = "AbortError";
      reject(abortError);
      return;
    }

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, ACCESS_LOAD_TIMEOUT_RETRY_DELAY_MS);

    const handleAbort = () => {
      clearTimeout(timeoutId);
      const abortError = new Error("The operation was aborted.");
      abortError.name = "AbortError";
      reject(abortError);
    };

    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

async function fetchAccessPayloadOnce({ expectedUserId = "", signal } = {}) {
  const abortController = new AbortController();
  const handleSignalAbort = () => {
    abortController.abort();
  };

  if (signal?.aborted) {
    abortController.abort();
  } else {
    signal?.addEventListener("abort", handleSignalAbort, { once: true });
  }

  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, ACCESS_LOAD_TIMEOUT_MS);

  try {
    const response = await fetchWithSupabaseAuth("/api/me/access", {
      cache: "no-store",
      signal: abortController.signal,
    });
    const payload = await response.json().catch(() => null);

    logAccessDebug("load:response", {
      ok: response.ok,
      payload,
      status: response.status,
    });

    if (!response.ok) {
      throw resolveAccessRequestError(undefined, {
        fallbackMessage: "Impossible de charger les accès.",
        payload,
        response,
      });
    }

    if (!isValidAccessPayload(payload, { expectedUserId })) {
      throw new AccessRequestError("La réponse des accès est incomplète.", {
        code: "invalid_payload",
      });
    }

    return payload;
  } catch (error) {
    if (abortController.signal.aborted && signal?.aborted) {
      throw error;
    }

    if (abortController.signal.aborted) {
      throw new AccessRequestError("Le chargement des accès a dépassé le délai attendu.", {
        code: "timeout",
      });
    }

    throw resolveAccessRequestError(error);
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", handleSignalAbort);
  }
}

async function fetchAccessPayload({ expectedUserId = "", signal } = {}) {
  try {
    return await fetchAccessPayloadOnce({ expectedUserId, signal });
  } catch (error) {
    const nextError = resolveAccessRequestError(error);
    if (nextError.code !== "timeout" || signal?.aborted) {
      throw nextError;
    }

    logAccessDebug("load:retry-after-timeout", {
      userId: expectedUserId || null,
    });
    await waitForAccessRetryDelay(signal);
    return fetchAccessPayloadOnce({ expectedUserId, signal });
  }
}

const AccessContext = React.createContext({
  access: EMPTY_ACCESS_STATE,
  activeRole: null,
  errorCode: "",
  errorMessage: "",
  errorStatus: null,
  hasAnyPermission: () => false,
  hasPermission: () => false,
  isAuthError: false,
  isLoading: false,
  isReady: true,
  permissions: [],
  reload: async () => EMPTY_ACCESS_STATE,
  session: null,
});

export function AccessProvider({ children = null }) {
  const [session, setSession] = React.useState(null);
  const [isSessionReady, setIsSessionReady] = React.useState(false);
  const [access, setAccess] = React.useState(EMPTY_ACCESS_STATE);
  const [status, setStatus] = React.useState("loading");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [errorMeta, setErrorMeta] = React.useState(EMPTY_ACCESS_ERROR_META);
  const accessRef = React.useRef(EMPTY_ACCESS_STATE);
  const sessionUserId = resolveAccessSessionUserId(session);
  const sessionUserIdRef = React.useRef(sessionUserId);
  sessionUserIdRef.current = sessionUserId;

  React.useEffect(() => {
    accessRef.current = access;
  }, [access]);

  const loadAccess = React.useCallback(async ({ signal } = {}) => {
    let payload;
    try {
      payload = await fetchAccessPayload({ expectedUserId: sessionUserId, signal });
    } catch (error) {
      const nextError = resolveAccessRequestError(error);
      if (nextError.status !== 401 || signal?.aborted) {
        throw nextError;
      }

      const { error: sessionError, session: confirmedSession } = await getSupabaseSession();
      if (sessionError || sessionUserIdRef.current !== sessionUserId || resolveAccessSessionUserId(confirmedSession) !== sessionUserId) {
        throw nextError;
      }

      logAccessDebug("load:retry-after-session-confirmation", {
        userId: sessionUserId || null,
      });
      // Bypass the automatic timeout retry: the session confirmation already
      // acted as a backoff before this second authenticated attempt.
      payload = await fetchAccessPayloadOnce({ expectedUserId: sessionUserId, signal });
    }

    if (signal?.aborted || sessionUserIdRef.current !== sessionUserId) {
      const abortError = new Error("The operation was aborted.");
      abortError.name = "AbortError";
      throw abortError;
    }

    const nextAccess = buildAccessState(payload);
    accessRef.current = nextAccess;

    writeAccessSessionCache(nextAccess);
    setAccess(nextAccess);
    setErrorMessage("");
    setErrorMeta(EMPTY_ACCESS_ERROR_META);
    setStatus("ready");
    logAccessDebug("load:success", {
      activeRole: nextAccess.activeRole || null,
      permissions: nextAccess.permissions,
      source: nextAccess.source || null,
      userId: nextAccess.userId || null,
    });
    return nextAccess;
  }, [sessionUserId]);

  const handleLoadFailure = React.useCallback(async (error, {
    expectedUserId = "",
    signal,
  } = {}) => {
    const isFailureStillCurrent = () => Boolean(expectedUserId)
      && !signal?.aborted
      && sessionUserIdRef.current === expectedUserId;

    if (!isFailureStillCurrent()) {
      return accessRef.current;
    }

    const nextError = resolveAccessRequestError(error);
    const nextErrorMeta = buildAccessErrorMeta(nextError);
    let previousAccess = accessRef.current;

    const preservePreviousAccess = (event, details = {}) => {
      setAccess(previousAccess);
      setErrorMessage("");
      setErrorMeta(EMPTY_ACCESS_ERROR_META);
      setStatus("ready");
      logAccessDebug(event, {
        activeRole: previousAccess.activeRole || null,
        code: nextErrorMeta.code || null,
        message: nextError.message,
        status: nextErrorMeta.status,
        userId: previousAccess.userId || null,
        ...details,
      });
      return previousAccess;
    };

    const applyFailure = (failureMeta, details = {}) => {
      accessRef.current = EMPTY_ACCESS_STATE;
      setAccess(EMPTY_ACCESS_STATE);
      setErrorMessage(nextError.message);
      setErrorMeta(failureMeta);
      setStatus("error");
      logAccessDebug("load:error", {
        code: failureMeta.code || null,
        isAuthError: failureMeta.isAuthError,
        message: nextError.message,
        status: failureMeta.status,
        ...details,
      });
      return EMPTY_ACCESS_STATE;
    };

    if (
      !nextErrorMeta.isAuthError
      && canPreserveResolvedAccessForUserId(previousAccess, expectedUserId)
    ) {
      return preservePreviousAccess("load:preserve-previous-access");
    }

    if (nextErrorMeta.isAuthError) {
      let confirmedSession = null;
      let sessionLookupFailed = false;
      try {
        const sessionResult = await getSupabaseSession();
        confirmedSession = sessionResult.session;
        sessionLookupFailed = Boolean(sessionResult.error);
      } catch {
        sessionLookupFailed = true;
      }

      if (!isFailureStillCurrent()) {
        return accessRef.current;
      }

      previousAccess = accessRef.current;
      if (sessionLookupFailed) {
        if (canPreserveResolvedAccessForUserId(previousAccess, expectedUserId)) {
          return preservePreviousAccess("load:preserve-after-session-check-failure");
        }

        return applyFailure({
          ...nextErrorMeta,
          code: "session_check_failed",
          isAuthError: false,
        }, {
          sessionCheckFailed: true,
        });
      }

      const confirmedSessionUserId = resolveAccessSessionUserId(confirmedSession);
      if (confirmedSessionUserId && confirmedSessionUserId !== expectedUserId) {
        return accessRef.current;
      }

      if (
        confirmedSession
        && canPreserveResolvedAccessForUserId(previousAccess, expectedUserId)
      ) {
        return preservePreviousAccess("load:preserve-after-auth-confirmation");
      }

      if (!confirmedSession) {
        clearAccessSessionCache();
      }

      return applyFailure(nextErrorMeta, {
        sessionConfirmedMissing: !confirmedSession,
      });
    }

    return applyFailure(nextErrorMeta);
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const applySession = (nextSession) => {
      if (!isMounted) {
        return;
      }

      const cachedAccessEntry = nextSession
        ? readAccessSessionCacheForSession(nextSession)
        : null;

      if (!nextSession) {
        clearAccessSessionCache();
      } else if (canPreserveResolvedAccessForSession(accessRef.current, nextSession)) {
        setStatus("ready");
      } else if (cachedAccessEntry?.access) {
        accessRef.current = cachedAccessEntry.access;
        setAccess(cachedAccessEntry.access);
        setErrorMessage("");
        setErrorMeta(EMPTY_ACCESS_ERROR_META);
        setStatus("ready");
      } else {
        setStatus("loading");
      }

      logAccessDebug("session:update", {
        hasSession: Boolean(nextSession),
        restoredFromCache: Boolean(cachedAccessEntry?.access),
        userId: resolveAccessSessionUserId(nextSession) || null,
      });
      setSession(nextSession ?? null);
      setIsSessionReady(true);
    };

    const unsubscribe = subscribeToSupabaseAuthChanges((nextSession) => {
      applySession(nextSession);
    });

    void getSupabaseSession()
      .then(({ session: nextSession }) => {
        applySession(nextSession);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        logAccessDebug("session:update", {
          hasSession: false,
          userId: null,
        });
        clearAccessSessionCache();
        setSession(null);
        setIsSessionReady(true);
      });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (!isSessionReady) {
      return undefined;
    }

    if (!session) {
      clearAccessSessionCache();
      setAccess(EMPTY_ACCESS_STATE);
      setErrorMessage("");
      setErrorMeta(EMPTY_ACCESS_ERROR_META);
      setStatus("ready");
      return undefined;
    }

    const abortController = new AbortController();
    const shouldBlockDuringRefresh = !canPreserveResolvedAccessForSession(accessRef.current, session);

    if (shouldBlockDuringRefresh) {
      setStatus("loading");
    }

    setErrorMessage("");
    setErrorMeta(EMPTY_ACCESS_ERROR_META);
    logAccessDebug("load:start", {
      blocking: shouldBlockDuringRefresh,
      source: "effect",
      userId: resolveAccessSessionUserId(session) || null,
    });

    void loadAccess({ signal: abortController.signal }).catch((error) => {
      if (abortController.signal.aborted || error?.name === "AbortError") {
        return;
      }

      void handleLoadFailure(error, {
        expectedUserId: sessionUserId,
        signal: abortController.signal,
      });
    });

    return () => {
      abortController.abort();
    };
  }, [handleLoadFailure, isSessionReady, loadAccess, session]);

  const reload = React.useCallback(async () => {
    if (!session) {
      clearAccessSessionCache();
      setAccess(EMPTY_ACCESS_STATE);
      setErrorMessage("");
      setErrorMeta(EMPTY_ACCESS_ERROR_META);
      setStatus("ready");
      return EMPTY_ACCESS_STATE;
    }

    setStatus("loading");
    setErrorMessage("");
    setErrorMeta(EMPTY_ACCESS_ERROR_META);
    logAccessDebug("load:start", {
      source: "reload",
      userId: resolveAccessSessionUserId(session) || null,
    });
    try {
      return await loadAccess();
    } catch (error) {
      return handleLoadFailure(error, {
        expectedUserId: sessionUserId,
      });
    }
  }, [handleLoadFailure, loadAccess, session]);

  React.useEffect(() => {
    logAccessDebug("state:changed", {
      activeRole: access.activeRole || null,
      errorCode: errorMeta.code || null,
      errorMessage: errorMessage || null,
      hasSession: Boolean(session),
      isAuthError: errorMeta.isAuthError,
      isSessionReady,
      permissionCount: Array.isArray(access.permissions) ? access.permissions.length : 0,
      status,
    });
  }, [access.activeRole, access.permissions, errorMessage, errorMeta.code, errorMeta.isAuthError, isSessionReady, session, status]);

  const contextValue = React.useMemo(() => ({
    access,
    activeRole: access.activeRole || null,
    errorCode: errorMeta.code,
    errorMessage,
    errorStatus: errorMeta.status,
    hasAnyPermission(permissionKeys) {
      return accessHasAnyPermission(access, permissionKeys);
    },
    hasPermission(permissionKey) {
      return accessHasPermission(access, permissionKey);
    },
    isAuthError: errorMeta.isAuthError,
    isLoading: !isSessionReady || status === "loading",
    isReady: isSessionReady && status !== "loading",
    permissions: access.permissions,
    reload,
    session,
  }), [access, errorMessage, errorMeta.code, errorMeta.isAuthError, errorMeta.status, isSessionReady, reload, session, status]);

  return (
    <AccessContext.Provider value={contextValue}>
      {children}
    </AccessContext.Provider>
  );
}

export default function useAccess() {
  return React.useContext(AccessContext);
}
